import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance = null;

// 1. 获取（并确保加载）单例 FFmpeg 实例
async function getFFmpeg() {
    if (!ffmpegInstance) {
        ffmpegInstance = new FFmpeg();
    }
    const ffmpeg = ffmpegInstance;

    if (!ffmpeg.loaded) {
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
        await ffmpeg.load({
            coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
    }
    return ffmpeg;
}

// 🎯 2. Web 版 "FFprobe"：检查视频是否符合 9:16 (1080x1920) 和 H264
export async function checkVideoDetails(inputFile) {
    try {
        const ffmpeg = await getFFmpeg();
        const inputName = `probe_${Date.now()}.mp4`;

        // 把文件写进 FFmpeg 虚拟内存
        await ffmpeg.writeFile(inputName, await fetchFile(inputFile));

        let logBuffer = '';
        // 捕获 FFmpeg 输出的元数据日志
        const logCallback = ({ message }) => { logBuffer += message + '\n'; };
        ffmpeg.on('log', logCallback);

        // 故意不给输出文件，只跑输入，FFmpeg 会打印元数据后报错退出
        await ffmpeg.exec(['-i', inputName]);

        // 释放监听和文件
        ffmpeg.off('log', logCallback);
        await ffmpeg.deleteFile(inputName);

        // --- 🔍 正则表达式精准匹配视频流信息 ---
        // 1. 匹配分辨率 (如: 1080x1920)
        const resolutionMatch = logBuffer.match(/Video:.*?\s(\d{3,4})x(\d{3,4})/);
        const width = resolutionMatch ? parseInt(resolutionMatch[1], 10) : 0;
        const height = resolutionMatch ? parseInt(resolutionMatch[2], 10) : 0;

        // 2. 匹配编码格式 (如: h264, avc1)
        const codecMatch = logBuffer.match(/Video:\s([a-zA-Z0-9]+)/);
        const codec = codecMatch ? codecMatch[1].toLowerCase() : '';

        // 3. 匹配时长 (如: Duration: 00:00:05.12)
        const durationMatch = logBuffer.match(/Duration:\s(\d+):(\d+):(\d+\.\d+)/);
        let durationMs = 0.0;
        if (durationMatch) {
            const hours = parseInt(durationMatch[1], 10);
            const minutes = parseInt(durationMatch[2], 10);
            const seconds = parseFloat(durationMatch[3]);
            durationMs = ((hours * 3600) + (minutes * 60) + seconds) * 1000;
        }

        const isCompliant = width === 1080 &&
            height === 1920 &&
            (codec === 'h264' || codec === 'avc1');

        return { isCompliant, durationMs };

    } catch (e) {
        console.warn("FFprobe 检查结束（正常行为拦截）");
        return { isCompliant: false, durationMs: 0.0 };
    }
}

// 🚀 3. 核心调用逻辑：完全对齐你的 processVideo 策略
export async function formatAndCropVideo(inputFile, onProgress) {
    if (typeof inputFile === 'string' && inputFile.startsWith('https://api.antaressocial.com')) {
        return resolve(inputFile);
    }
    const details = await checkVideoDetails(inputFile);

    // 🚀 Step 2: 完美复刻你的 Flutter 逻辑 
    // 如果符合 1080x1920 并且是 H264 编码
    if (details.isCompliant) {
        // ✨ 直接读取原生 File/Blob 对象的 size 属性进行大小校验 (100MB = 100 * 1024 * 1024 字节)
        const fileSizeInBytes = inputFile.size || 0;
        const hundredMegaBytes = 100 * 1024 * 1024;

        if (fileSizeInBytes < hundredMegaBytes) {
            console.log(`🚀 视频完美合规且体积过关 (${(fileSizeInBytes / 1024 / 1024).toFixed(2)}MB < 100MB)，免试直通返回！`);
            if (onProgress) onProgress(100);
            return inputFile; // 拦截成功，直接放行原文件
        } else {
            console.log(`⚠️ 视频虽然格式合规，但体积超标 (${(fileSizeInBytes / 1024 / 1024).toFixed(2)}MB >= 100MB)，强制送去压缩转码...`);
        }
    }

    // 🛠️ Step 3: 条件不满足，启动完整的 WASM 转码压缩
    console.log("启动 WASM 核心进行全渠道合规重编码渲染...");
    const ffmpeg = await getFFmpeg();

    if (onProgress) {
        ffmpeg.on('progress', ({ progress }) => {
            const percentage = Math.round(progress * 100);
            onProgress(Math.min(percentage, 100));
        });
    }

    const inputName = `in_${Date.now()}.mp4`;
    const outputName = `out_${Date.now()}.mp4`;

    await ffmpeg.writeFile(inputName, await fetchFile(inputFile));

    // 执行转码过滤链（限时 60 秒，并压制码率到 5M，强制输出标准 1080x1920 补黑边）
    await ffmpeg.exec([
        '-i', inputName,
        '-t', '60',
        '-vf', 'scale=1080:1920:force_original_aspect_ratio=decrease,pad=1080:1920:(ow-iw)/2:(oh-ih)/2:black',
        '-vcodec', 'libx264',
        '-b:v', '5M',          // 对齐你 Flutter 里的 '-b:v', '5M' 码率控制，强行把体积压缩下来
        '-pix_fmt', 'yuv420p',
        '-f', 'mp4',
        outputName
    ]);

    const data = await ffmpeg.readFile(outputName);

    // 清理虚拟内存现场
    await ffmpeg.deleteFile(inputName);
    await ffmpeg.deleteFile(outputName);
    ffmpeg.off('progress');

    // 返回转码压缩后的干净二进制 Blob
    return new Blob([data.buffer], { type: 'video/mp4' });
}